<template>
  <div class="comment" :id="'comment-' + comment.id">
    <NuxtLink
      v-if="comment.user"
      :to="
        comment.user.organizationSlug
          ? '/partners/' + encodeURI(comment.user.organizationSlug)
          : '/users/' + encodeURI(comment.user.userName)
      "
      class="comment__user-link"
    >
      <UserImage
        :imageId="comment.user.profileImageId"
        class="comment__user-image"
      />
      <div class="comment__user-info">
        <div class="comment__user-header">
          <p class="comment__user-name">
            {{
              comment.user.organizationName
                ? comment.user.organizationName
                : comment.user.userName
            }}
          </p>
          <img
            v-if="comment.user.verified"
            src="~assets/icon/verified.svg"
            :alt="$t('accessibility.verified')"
            class="comment__verified-icon"
          />
        </div>
        <p class="comment__date">
          {{ $d(new Date(comment.createdOn), 'short') }}
        </p>
      </div>
    </NuxtLink>
    <div v-else class="comment__deleted-user">
      <UserImage />
      <div class="comment__user-header">
        <p class="comment__user-name">
          {{ $t('spotdetail.deleted_user_title') }}
        </p>
      </div>
    </div>
    <div
      ref="commentText"
      v-if="!editing"
      class="comment__text"
      :class="comment.deletedOn ? 'comment__text--deleted' : ''"
      v-linkify:options="{
        target: '_blank',
      }"
      v-html="textHelpers.replaceNewlineWithHtmlTags(comment.text)"
    ></div>
    <div v-else class="comment__edit">
      <textarea
        ref="editTextArea"
        class="comment__edit-textarea"
        v-model="comment.text"
        @input="updateEditTextAreaHeight()"
        @focus="updateEditTextAreaHeight()"
      ></textarea>
      <div class="comment__edit-actions">
        <ButtonPrimary
          @click="toggleEdit()"
          :small="true"
          v-if="loggedIn && commentBelongsToUser()"
          >{{ $t('general.cancel') }}
        </ButtonPrimary>
        <ButtonSubmit
          :small="true"
          :loading="uploadingComment"
          :error="uploadingCommentError"
          @click="editComment(comment)"
        />
      </div>
    </div>
    <div v-if="comment.deletedOn" class="comment__meta comment__meta--deleted">
      {{
        $t('spotdetail.deleted') +
        ': ' +
        $d(new Date(comment.deletedOn), 'long')
      }}
    </div>
    <div
      v-else-if="comment.editedOn"
      class="comment__meta comment__meta--edited"
    >
      {{
        $t('spotdetail.edited') + ': ' + $d(new Date(comment.editedOn), 'long')
      }}
    </div>
    <div class="comment__actions">
      <CommentVote
        v-if="!comment.deletedOn"
        :comment="comment"
        :voted="voted"
      />
      <div
        class="comment__action-buttons"
        :class="editing ? 'comment__action-buttons--hidden' : ''"
      >
        <button
          class="comment__action__button"
          @click="toggleEdit()"
          :class="editing ? 'invisible' : 'visible'"
          v-if="loggedIn && commentBelongsToUser() && !comment.deletedOn"
        >
          <Icon size="1.5em" name="material-symbols:edit" />
          {{ $t('spotdetail.comments_edit_button_title') }}
        </button>
        <button
          v-if="loggedIn && !comment.parentId"
          class="comment__action__button"
          :class="commentInputHidden ? '' : 'hidden'"
          @click="toggleCommentFormVisibility()"
        >
          <Icon size="1.5em" name="material-symbols:reply" />
          {{ $t('spotdetail.comments_reply_button_title') }}
        </button>
        <button
          class="comment__action__button"
          v-if="
            loggedIn &&
            commentBelongsToUser() &&
            !comment.deletedOn &&
            spot.officialAnswer?.id != comment.id
          "
          @click="showDeleteDialog(comment.id)"
        >
          <Icon size="1.5em" name="material-symbols:delete" />
          {{ $t('spotdetail.delete_comment_button_title') }}
        </button>
        <CommentReport
          v-if="loggedIn && !commentBelongsToUser() && !comment.deletedOn"
          :spot="spot"
          :comment="comment"
        />
        <Modal :id="'delete_comment_modal_' + comment.id">
          <div class="comment__delete-modal">
            <h3 class="comment__delete-title">
              {{ $t('spotdetail.delete_comment_title') }}
            </h3>
            <p class="comment__delete-text">
              {{ $t('spotdetail.delete_comment_text') }}
            </p>
            <ButtonSubmit
              :loading="deletingComment"
              :error="deletingCommentError"
              @click="deleteComment(comment)"
              :title="$t('spotdetail.delete_comment_button_title')"
            />
          </div>
        </Modal>
        <CommentOfficialAnswer
          v-if="
            loggedIn &&
            commentBelongsToUser() &&
            spot.userData?.canMarkCommentOfficial &&
            !comment.deletedOn
          "
          :officialdAnswerId="spot.officialAnswer?.id"
          :comment="comment"
          :user="user"
          :spot="spot"
        >
        </CommentOfficialAnswer>
      </div>
    </div>
    <div
      v-if="!comment.parentId && comment.replies.length > 0"
      class="comment__replies"
    >
      <CommentListElement
        v-for="reply of comment.replies"
        :comment="reply"
        :spot="spot"
        class="comment__reply"
      />
    </div>
    <Transition>
      <div
        v-if="loggedIn && !comment.parentId && !commentInputHidden"
        class="comment__input"
        :id="'comment-input-' + comment.id"
      >
        <CommentAdd
          @submit="toggleCommentFormVisibility()"
          @dismiss="toggleCommentFormVisibility()"
          :user="user"
          :spot="spot"
          :comment="comment"
          :allowCancel="true"
        />
      </div>
    </Transition>
  </div>
</template>
<script setup lang="ts">
const { $api } = useNuxtApp();

const { loggedIn, user } = useUserSession();
const props = defineProps<{ comment: Comment; spot: SpotDetail }>();

const editing = ref(false);
const uploadingCommentError = ref(false);
const uploadingComment = ref(false);
const deletingComment = ref(false);
const deletingCommentError = ref(false);
const editTextArea = ref();
const commentText = ref();
const commentInputHidden = ref(true);
const voted = ref(
  loggedIn && props.spot.userData && props.spot.userData.votedComments
    ? props.spot.userData.votedComments.includes(props.comment.id)
    : false
);
const comment = ref(props.comment);

function toggleEdit() {
  editing.value = !editing.value;
}

function showDeleteDialog(id: number) {
  (
    document.getElementById('delete_comment_modal_' + id) as HTMLFormElement
  ).showModal();
}

function commentBelongsToUser() {
  return comment.value?.user && user.value?.id === comment.value?.user.id;
}

function toggleCommentFormVisibility() {
  commentInputHidden.value = !commentInputHidden.value;
}

function editComment(theComment: Comment) {
  uploadingComment.value = true;

  const formData = new FormData();
  formData.append('text', theComment.text.valueOf());

  const url = '/comments/' + theComment.id.toString() + '/edit';

  $api<Comment>(url, { method: 'POST', body: formData })
    .then(function (response) {
      uploadingComment.value = false;
      uploadingCommentError.value = false;
      theComment.text = response.text;
      theComment.editedOn = response.editedOn;
      editing.value = false;

      sendUpdateEvent(theComment);
    })
    .catch(function (error) {
      uploadingComment.value = false;
      error.value = true;
      console.log(error);
      editing.value = false;
    });
}

function sendUpdateEvent(theComment: Comment) {
  if(!theComment.user?.official) {
    return;
  }
  const event = new CustomEvent('comment-updated', {
    detail: theComment,
  });
  document.dispatchEvent(event);
}

function deleteComment(theComment: Comment) {
  deletingComment.value = true;

  const url = '/comments/' + theComment.id.toString();

  $api<Comment>(url, { method: 'DELETE' })
    .then((theComment: Comment) => {
      deletingComment.value = false;
      comment.value = theComment;
      setTimeout(() => {
        (
          document.getElementById(
            'delete_comment_modal_' + theComment.id
          ) as HTMLFormElement
        ).close();
      }, 500);
    })
    .catch(function (error) {
      deletingComment.value = false;
      deletingCommentError.value = true;
    });
}

function updateEditTextAreaHeight() {
  editTextArea.value.style.height = 'auto';
  editTextArea.value.style.height = 5 + editTextArea.value.scrollHeight + 'px';
}
</script>
<style lang="scss">
// Transition animations
// .v-enter-from {
//   opacity: 0;
// }

// .v-enter-to {
//   opacity: 1;
// }

// .v-enter-active {
//   transition: opacity 0.2s;
// }

.comment {
  border-left: 1px solid rgb(var(--primary));
  border-top-width: 0;
  padding-left: 1rem;

  &__user-link {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  &__user-image {
    width: 1.75rem;
    height: 1.75rem;
    border: none;
  }

  &__user-info {
    display: flex;
    gap: 0.5rem;
  }

  &__user-header {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  &__user-name {
    font-weight: 600;
    font-size: 0.75rem;
  }

  &__verified-icon {
  }

  &__date {
    font-size: 0.75rem;
    color: rgb(var(--secondary));
  }

  &__deleted-user {
    display: flex;
    gap: 0.25rem;
    align-items: center;
  }

  &__text {
    margin-top: 1rem;
    overflow: hidden;

    &--deleted {
      font-style: italic;
      color: rgb(var(--secondary));
      padding-left: 0.5rem;
    }
  }

  &__edit {
    padding-top: 0.25rem;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
  }

  &__edit-textarea {
    width: 100%;
    height: fit-content;
    padding: 0.125rem;
    border: 1px solid rgb(var(--theme-primary));
    background-color: var(--theme-bg);
    min-height: 6em;
    background-color: white;
  }

  &__edit-actions {
    display: flex;
    gap: 0.25rem;
  }

  &__meta {
    font-size: 0.75rem;
    color: rgb(var(--secondary));
    margin-top: 1rem;

    &--deleted {
    }

    &--edited {
    }
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
  }

  &__action__button {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    color: rgb(var(--secondary));
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    transition: background-color 0.1s;

    &:hover {
      background-color: rgba(#fff, 0.75);
    }
    &:disabled {
      opacity: 0.75;
      pointer-events: none;
    }
  }

  &__icon {
    display: flex;
    gap: 0.125rem;
    align-items: center;
  }

  &__icon-wrapper {
  }

  &__icon-svg {
    height: 16px;
  }

  &__action-buttons {
    display: flex;
    gap: 0.75rem;
    flex-grow: 1;

    &--hidden {
      display: none;
    }
  }

  &__action-spacer {
    flex-grow: 1;
  }

  &__delete-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__delete-title {
  }

  &__delete-text {
  }

  &__replies {
    margin-bottom: 0.25rem;
    margin-left: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-top: 2rem;

    .comment {
      // border-top: 1px dashed rgb(var(--secondary));
    }
  }

  &__reply {
    padding-right: 0;
  }

  &__input {
    margin-left: 1rem;
    padding-top: 0.5rem;
    margin-top: 0.5rem;
  }
}

:deep(.comment__text) {
  a {
    color: rgb(var(--theme-primary));
    text-decoration: underline;
  }
}
</style>
